import { useRef } from "react";
import { motion, useInView, Variants } from "framer-motion";
import { Check, X, Zap } from "lucide-react";

interface ComparisonRow {
  feature: string;
  fanjago: boolean;
  whatsapp: boolean;
  spreadsheets: boolean;
}

const comparisonData: ComparisonRow[] = [
  {
    feature: "Centralized Player Data",
    fanjago: true,
    whatsapp: false,
    spreadsheets: false,
  },
  {
    feature: "Real-time Team Communication",
    fanjago: true,
    whatsapp: true,
    spreadsheets: false,
  },
  {
    feature: "Automated Payment Tracking",
    fanjago: true,
    whatsapp: false,
    spreadsheets: false,
  },
  {
    feature: "Match Scheduling & Fixtures",
    fanjago: true,
    whatsapp: false,
    spreadsheets: true,
  },
  {
    feature: "Performance Analytics",
    fanjago: true,
    whatsapp: false,
    spreadsheets: false,
  },
  {
    feature: "Parent Updates & Notifications",
    fanjago: true,
    whatsapp: true,
    spreadsheets: false,
  },
  {
    feature: "Digital Player Passport",
    fanjago: true,
    whatsapp: false,
    spreadsheets: false,
  },
  {
    feature: "AI Video Highlights",
    fanjago: true,
    whatsapp: false,
    spreadsheets: false,
  },
];

const CheckIcon = () => (
  <motion.div
    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent/20 text-accent"
    whileHover={{ scale: 1.2 }}
    transition={{ duration: 0.2 }}
  >
    <Check className="w-5 h-5" strokeWidth={3} />
  </motion.div>
);

const XIcon = () => (
  <motion.div
    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-destructive/10 text-destructive/60"
    whileHover={{ scale: 1.1 }}
    transition={{ duration: 0.2 }}
  >
    <X className="w-5 h-5" strokeWidth={2} />
  </motion.div>
);

const ComparisonTable = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const rowVariants: Variants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
    },
  };

  return (
    <section
      ref={sectionRef}
      className="min-h-screen flex items-center py-12 px-6 bg-surface/30 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-accent/5 blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.05, 0.1, 0.05],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container mx-auto max-w-5xl relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/30 text-accent text-sm font-semibold mb-6"
            initial={{ scale: 0.9 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Zap className="w-4 h-4" />
            <span>The Smart Choice</span>
          </motion.div>
          <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
            Why Choose FanJago?
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Stop juggling between apps. One platform for everything football.
          </p>
        </motion.div>

        {/* Comparison table */}
        <motion.div
          className="bg-surface/50 backdrop-blur-xl rounded-2xl border border-border/50 overflow-hidden shadow-premium-lg"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Table header */}
          <div className="grid grid-cols-4 gap-4 p-6 bg-accent/5 border-b border-border/50">
            <div className="text-left">
              <span className="text-sm font-medium text-muted-foreground">
                Features
              </span>
            </div>
            <div className="text-center">
              <span className="font-heading font-bold text-lg text-accent">
                FanJago
              </span>
            </div>
            <div className="text-center">
              <span className="font-medium text-muted-foreground">
                WhatsApp
              </span>
            </div>
            <div className="text-center">
              <span className="font-medium text-muted-foreground">
                Spreadsheets
              </span>
            </div>
          </div>

          {/* Table rows */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {comparisonData.map((row, index) => (
              <motion.div
                key={row.feature}
                variants={rowVariants}
                className={`grid grid-cols-4 gap-4 p-6 items-center transition-colors duration-300 hover:bg-accent/5 ${
                  index !== comparisonData.length - 1
                    ? "border-b border-border/30"
                    : ""
                }`}
              >
                <div className="text-left">
                  <span className="text-sm md:text-base font-medium text-foreground">
                    {row.feature}
                  </span>
                </div>
                <div className="flex justify-center">
                  {row.fanjago ? <CheckIcon /> : <XIcon />}
                </div>
                <div className="flex justify-center">
                  {row.whatsapp ? <CheckIcon /> : <XIcon />}
                </div>
                <div className="flex justify-center">
                  {row.spreadsheets ? <CheckIcon /> : <XIcon />}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Bottom CTA hint */}
        <motion.p
          className="text-center text-muted-foreground mt-8"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          Ready to upgrade your football management?
          <span className="text-accent font-semibold">
            {" "}
            Get started for free.
          </span>
        </motion.p>
      </div>
    </section>
  );
};

export default ComparisonTable;
